import { print } from '@apollo/client/utilities';

import { PortalSessionDocument } from '@willow/graphql-iso/src/portal';
import { Sentry } from '@willow/shared-web';

// Relative in every environment. Local dev reaches the app server through the Vite proxy, which
// keeps the SPA and the API on one origin so the same-origin check can be exercised.
export const PORTAL_GRAPHQL_ENDPOINT = '/api/portal/graphql';

export const PORTAL_SESSION_PROBE_TIMEOUT_MS = 5000;

export type PortalSessionProbeResult = 'SIGNED_IN' | 'SIGNED_OUT' | 'UNAVAILABLE';

const PROBE_BODY = JSON.stringify({ operationName: 'portalSession', query: print(PortalSessionDocument) });

// A plain fetch rather than the app's Apollo client on purpose: that client retries a 401 for
// several seconds, which would hold the login form back that long for every signed-out borrower.
export const probePortalSession = async (): Promise<PortalSessionProbeResult> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PORTAL_SESSION_PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(PORTAL_GRAPHQL_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', 'x-original-host': window.location.hostname },
      body: PROBE_BODY,
      signal: controller.signal,
    });
    if (response.status === 401) return 'SIGNED_OUT';
    if (!response.ok) throw new Error(`Portal session probe failed with status ${response.status}`);
    const { errors }: { errors?: unknown } = await response.json();
    if (errors) throw new Error('Portal session probe returned GraphQL errors', { cause: errors });
    return 'SIGNED_IN';
  } catch (error: unknown) {
    Sentry.captureException(error);
    return 'UNAVAILABLE';
  } finally {
    clearTimeout(timeout);
  }
};
